import React from 'react';
import { ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { UrlGenerator } from '@/utils/url-generator.util';
import { PublicNavbar } from '@/components/layout/public/PublicNavbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { H1, H2, Body } from '@/components/ui/typography';
import { useThemeImage } from '@/hooks/use-theme-image';
import { publicImages } from '@/config/images.config';

const OrganizationNotFound: React.FC = () => {
  const workspaceImage = useThemeImage(publicImages.auth.workspace);

  const handleOnboarding = () => {
    // Redirect to onboarding wizard using environment-aware URL
    window.location.href = UrlGenerator.getPlatformUrl('/onboarding');
  };

  const handleExploreFeatures = () => {
    // Redirect to main platform using environment-aware URL
    window.location.href = UrlGenerator.getPlatformUrl('/');
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Background Image */}
      <div className="fixed inset-0 z-0">
        <img
          src={workspaceImage}
          alt="Professional workspace"
          className="w-full h-full object-cover"
          loading="eager"
        />
        {/* Theme-aware gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60 dark:from-background/90 dark:via-background/75 dark:to-background/50" />
        {/* Additional dark overlay for better text contrast on image */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 dark:from-primary/30 dark:via-transparent dark:to-secondary/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col min-h-screen">
        <PublicNavbar showLoginButton={true} />
        <div className="flex-1 flex overflow-hidden pt-20">
          {/* Left Side - Motivational Content */}
          <div className="hidden lg:flex lg:w-1/2 relative items-center">
            <div className="p-8 lg:p-12 w-full">
              <div className="space-y-6 max-w-lg">
                {/* Brand */}
                <H1 className="text-5xl lg:text-6xl font-bold text-foreground drop-shadow-sm">
                  VIDYANTRA
                </H1>

                {/* Heading */}
                <H2 className="text-2xl lg:text-3xl font-semibold text-foreground drop-shadow-sm">
                  Start Your Journey with Vidhyantra Today
                </H2>

                {/* Motivational Text */}
                <Body className="text-lg text-foreground/90 drop-shadow-sm">
                  Transform your educational institution with our comprehensive school management system. 
                  Join thousands of schools that have streamlined their operations and enhanced their efficiency.
                </Body>

                {/* Benefits List */}
                <div className="space-y-4 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 dark:bg-primary/30 backdrop-blur-sm flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    </div>
                    <Body className="text-lg text-foreground">Complete School Management Solution</Body>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 dark:bg-primary/30 backdrop-blur-sm flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    </div>
                    <Body className="text-lg text-foreground">Easy Setup & Quick Onboarding</Body>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 dark:bg-primary/30 backdrop-blur-sm flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    </div>
                    <Body className="text-lg text-foreground">24/7 Support & Regular Updates</Body>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 dark:bg-primary/30 backdrop-blur-sm flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    </div>
                    <Body className="text-lg text-foreground">Secure & Scalable Platform</Body>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="pt-6">
                  <Body className="text-base text-foreground/80 drop-shadow-sm">
                    Start onboarding now and get started in minutes. 
                    Our team is ready to help you every step of the way.
                  </Body>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Error Message & Onboarding CTA */}
          <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-12 overflow-y-auto">
            <div className="w-full max-w-md sm:max-w-lg lg:max-w-md py-4 sm:py-8 mt-8">
              <Card className="border shadow-lg transition-all duration-300 ease-in-out">
                <CardContent className="p-6 sm:p-8 lg:p-10">
                  {/* Error Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-destructive/10 dark:bg-destructive/20 flex items-center justify-center">
                      <AlertCircle className="w-8 h-8 text-destructive" />
                    </div>
                  </div>

                  {/* Error Message */}
                  <div className="text-center mb-6">
                    <H2 className="text-2xl font-semibold mb-3 text-foreground">
                      Organization Not Found
                    </H2>
                    <Body className="text-base text-muted-foreground">
                      We couldn't find the organization associated with this URL.
                    </Body>
                  </div>

                  {/* Possible Causes */}
                  <div className="mb-6 p-4 bg-muted/50 rounded-lg">
                    <Body className="text-sm font-medium mb-3 text-foreground">
                      This could happen if:
                    </Body>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-destructive mt-1">•</span>
                        <span>The organization name in the URL is incorrect</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-destructive mt-1">•</span>
                        <span>The organization hasn't been set up yet</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-destructive mt-1">•</span>
                        <span>The organization may have been deactivated</span>
                      </li>
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 mb-6">
                    <Button
                      onClick={handleOnboarding}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 text-base"
                      size="lg"
                    >
                      Start Onboarding
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>

                    <Button
                      onClick={handleExploreFeatures}
                      variant="outline"
                      className="w-full border-2 py-6 text-base"
                      size="lg"
                    >
                      Explore Our Features
                    </Button>
                  </div>

                  {/* Motivational Section */}
                  <div className="border-t pt-6">
                    <H2 className="text-lg font-semibold mb-2 text-center text-foreground">
                      Ready to Get Started?
                    </H2>
                    <Body className="text-sm text-center text-muted-foreground mb-4">
                      Join thousands of educational institutions using Vidhyantra to manage their operations efficiently.
                    </Body>
                    <div className="text-center">
                      <Button
                        onClick={handleOnboarding}
                        variant="link"
                        className="text-primary hover:text-primary/80 font-medium"
                      >
                        Start onboarding now →
                      </Button>
                    </div>
                  </div>

                  {/* Support Contact */}
                  <div className="mt-6 pt-6 border-t text-center">
                    <Body className="text-xs text-muted-foreground">
                      Need help? Contact us at{' '}
                      <a
                        href="mailto:support@vidhyantra.com"
                        className="text-primary hover:text-primary/80 underline font-medium"
                      >
                        support@vidhyantra.com
                      </a>
                    </Body>
                  </div>
                </CardContent>
              </Card>

              {/* Footer */}
              <div className="mt-6 text-center">
                <Body className="text-xs text-muted-foreground">
                  © 2024 Vidhyantra. All rights reserved.
                </Body>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationNotFound;
